import React, { useEffect, useState } from "react";
import { icons } from "../../../Assets/Icons/icons";
import AddOneButton from "../../common/AddOneButton";
import { createPortal } from "react-dom";
import DeleteAlertModal from "../../common/DeleteAlertModal";
import axios from "axios";
import { Pencil, Trash2 } from "lucide-react";
import AddOrEditDiscountModal from "../AdminDiscount/AddOrEditDiscountModal";

const AdminDiscountSkeleton = () => (
  <div className="">
    <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 mt-5 justify-between gap-6">
      {[...Array(6)].map((_, index) => (
        <div key={index} className="w-full group rounded-lg overflow-hidden">
          <div className="w-full h-56 bg-gray-300 rounded-lg animate-pulse"></div>
        </div>
      ))}
    </div>
  </div>
);

const AdminDiscountList = () => {
  const [isAddModal, setIsAddModal] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(null); 
  const [discounts, setDiscounts] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const [deleteModal, setDeleteModal] = useState(false);
  const [dltLoading, setDltLoading] = useState(false);
  const [selectedDelete, setSelectedDelete] = useState(null);

  // Fetch all discounts
  const fetchDiscounts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/discount/get-all-discount`
      );
      setDiscounts(response.data || []);
    } catch (error) {
      setError(error?.response?.data?.message || "Failed to fetch discounts");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiscounts();
  }, []);

  // Delete Discount
  const handleDelete = async () => {
    if (!selectedDelete) return;
    try {
      setDltLoading(true);
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/discount/delete-discount/${selectedDelete._id}`
      );
      setDeleteModal(false);
      fetchDiscounts();
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete discount!");
    } finally {
      setDltLoading(false);
    }
  };

  return (
    <>
      <section>
        <div className="flex flex-col md:flex-row md:items-center space-y-3 md:space-y-0 justify-between">
          <h1 className="text-3xl flex gap-1 items-center capitalize font-philosopher">
            <span className="text-primaryColor">{icons.discountIcon || "🎯"}</span> All Discounts
          </h1>
          <AddOneButton
            label={"Add Discount"}
            onClick={() => {
              setSelectedDiscount(null);
              setIsAddModal(true);
            }}
          />
        </div>

        <div className="min-h-screen mt-5">
          {loading ? (
            <AdminDiscountSkeleton />
          ) : error ? (
            <div className="text-center text-red-500">{error}</div>
          ) : discounts?.length > 0 ? (
            <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 mt-3 justify-between gap-6">
              {discounts.map((discount, index) => (
                <div
                  key={discount._id}
                  className="relative w-full h-64 group rounded-lg overflow-hidden border border-gray-200 shadow-sm"
                  style={{ 
                    backgroundColor: discount.color || '#f3f4f6',
                    backgroundImage: discount.backgroundImage ? `url(${discount.backgroundImage})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  {/* Discount Content */}
                  <div className="relative w-full h-full p-6 flex flex-col justify-between">
                    {discount.image && (
                      <div className="flex justify-center mb-4">
                        <img 
                          src={discount.image} 
                          alt={discount.title}
                          className="h-20 w-20 object-contain"
                        />
                      </div>
                    )}
                    
                    <div className="text-center flex-1 flex flex-col justify-center">
                      <h3 className="text-2xl font-bold text-black mb-2">
                        {discount.title}
                      </h3>
                      <p className="text-orange-600 mb-3">
                        {discount.subtitle}
                      </p>
                      <div className="text-3xl font-bold text-red-600">
                        {discount.discountText}
                      </div>
                    </div>

                    {/* Overlay Buttons */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex justify-center items-center gap-3">
                      <button
                        onClick={() => {
                          setSelectedDiscount(discount);
                          setIsAddModal(true);
                        }}
                        className="p-2 bg-white/80 rounded-full text-[#6278e8] hover:bg-white hover:scale-110 transition-transform"
                      >
                        <Pencil className="w-4 h-4 text-[#6278e8]" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedDelete(discount);
                          setDeleteModal(true);
                        }}
                        className="p-2 bg-white/80 rounded-full text-red-500 hover:bg-white hover:scale-110 transition-transform"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="text-6xl text-gray-300 mb-4">🎯</div>
              <h2 className="text-2xl text-gray-500 mb-2">No Discounts Found</h2>
              <p className="text-gray-400 mb-6">
                Get started by creating a new discount offer
              </p>
              <AddOneButton
                onClick={() => setIsAddModal(true)}
                label="Add Discount"
              />
            </div>
          )}
        </div>
      </section>

      {/* Add / Edit Discount Modal - FIXED PROP NAMES */}
      {isAddModal &&
        createPortal(
          <AddOrEditDiscountModal
            isOpen={isAddModal}  // ✅ Changed from isAddModal to isOpen
            onClose={() => setIsAddModal(false)}
            onSuccess={fetchDiscounts}  // ✅ Changed from fetchDiscounts to onSuccess
            data={selectedDiscount}  // ✅ Changed from editData to data
          />,
          document.body
        )}

      {/* Delete Alert Modal */}
      {deleteModal && (
        <DeleteAlertModal
          isOpen={deleteModal}
          onClose={() => setDeleteModal(false)}
          dltType="discount"
          dltName={selectedDelete?.title}
          onConfirm={handleDelete}
          dltLoading={dltLoading}
        />
      )}
    </>
  );
};

export default AdminDiscountList;